import React, { useMemo, type JSX } from 'react';
import Dialog from '../../BaseComponents/Dialog/EDDialog';
import { makeStyles } from 'tss-react/mui';
import { Theme, DialogTitle, Typography } from '@mui/material';
import EDIconButton from '../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDButton from '../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../BaseComponents/Icon/EDIcon';

interface IProps {
  open: boolean;
  close: (event: React.MouseEvent) => void;
  onEntered: () => void;
  onCancel: (event: React.MouseEvent) => void;
  headerArea?: JSX.Element;
  headerAreaRef?: (node: HTMLDivElement) => void;
  navigatePrev?: () => void;
  navigateNext?: () => void;
  currentView?: number;
  totalViews?: number;
}

const useStyles = makeStyles()((theme: Theme) => ({
  bottomArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  navigateButton: {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  navigateContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
  },
  dialogHeader: {
    background: theme.palette.background.default,
    padding: `0 ${theme.spacing(2)}`,
  },
  headerTitle: {},
  headerWrapper: {
    color: theme.palette.text.primary,
    position: 'absolute',
    left: '50%',
    top: theme.spacing(3.5),
    transform: 'Translate(-50%, -50%)',
  },
  contentWrapper: {},
  topContainer: {
    margin: theme.spacing(0),
    display: 'flex',
    flex: 1,
  },
  searchBar: {
    display: 'flex',
    flex: 1,
    position: 'relative',
    paddingTop: theme.spacing(0),
    marginTop: theme.spacing(-1.25),
  },
  headerBottomArea: {
    marginTop: theme.spacing(0),
  },
  contentRoot: {
    display: 'flex',
  },
  flexContainer: {
    display: 'flex',
    flex: 1,
  },
}));

/**
 * En standardiserad dialog som är menad att öppnas som ett helskärmsläge på små skärmar. Används för närvarande till t.ex. autocomplete.
 */
const TouchFullscreen: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes } = useStyles(undefined, {
    props: props as any,
  });
  const navigateBeforeColor = useMemo(
    () => (props.currentView && props.currentView > 1 ? 'primary' : 'action'),
    [props.currentView]
  );
  const navigateNextColor = useMemo(
    () =>
      props.currentView &&
      props.totalViews &&
      props.currentView < props.totalViews
        ? 'primary'
        : 'action',
    [props.currentView, props.totalViews]
  );
  return (
    <>
      <Dialog
        open={props.open}
        onClose={event => props.close(event as React.MouseEvent)}
        headerBottomArea={
          <div className={classes.bottomArea}>
            <EDButton
              label="KLAR"
              color="primary"
              margin={0.75}
              padding={1.5}
              onClick={props.close}
              flat
            />
            <DialogTitle className={classes.headerWrapper}>
              <h5 className={classes.headerTitle}>Fyll i fält</h5>
            </DialogTitle>
            <div className={classes.navigateContainer}>
              {props.navigatePrev && (
                <EDIconButton
                  tooltip=""
                  icon={
                    <EDIcon
                      iconType="chevron_left"
                      color={navigateBeforeColor}
                      size="default"
                    />
                  }
                  iconSize="medium"
                  onClick={props.navigatePrev}
                  disabled={props.currentView === 1}
                  className={classes.navigateButton}
                />
              )}
              {props.currentView !== undefined &&
                props.totalViews !== undefined && (
                  <Typography color="textPrimary">
                    {props.currentView} av {props.totalViews}
                  </Typography>
                )}
              {props.navigateNext && (
                <div>
                  <EDIconButton
                    tooltip=""
                    icon={
                      <EDIcon
                        iconType="chevron_right"
                        color={navigateNextColor}
                        size="default"
                      />
                    }
                    iconSize="medium"
                    onClick={props.navigateNext}
                    disabled={Boolean(
                      props.currentView !== undefined &&
                        props.totalViews &&
                        props.currentView === props.totalViews
                    )}
                    className={classes.navigateButton}
                  />
                </div>
              )}
            </div>
          </div>
        }
        classes={{
          dialogHeader: classes.dialogHeader,
          bottomArea: classes.headerBottomArea,
          contentWrapper: classes.flexContainer,
          content: classes.contentRoot,
        }}
        disableRestoreFocus
        noCloseIcon
        fullscreen
        onEntered={props.onEntered}
      >
        <div className={classes.topContainer}>
          {props.headerArea && props.headerArea}
          <div className={classes.searchBar}>{props.children}</div>
        </div>
      </Dialog>
    </>
  );
};

export default TouchFullscreen;
